import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiClient';

const STATE_STYLE = {
  mitigated: 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-900',
  in_progress: 'bg-accent/10 text-accent-text border-accent/40',
  accepted: 'bg-surface-raised text-ink-secondary border-border-strong',
  unmitigated: 'bg-red-50 text-red-800 border-red-300 dark:bg-red-950/60 dark:text-red-200 dark:border-red-800',
};

const SEVERITY_WIDTH = {
  negligible: 10,
  low: 25,
  medium: 50,
  high: 80,
  critical: 100,
  unknown: 5,
};

const SEVERITY_COLOR = (level) => {
  const l = String(level || '').toLowerCase();
  if (l === 'negligible' || l === 'low') return 'bg-emerald-500 dark:bg-emerald-600';
  if (l === 'medium') return 'bg-amber-500 dark:bg-amber-600';
  if (l === 'high' || l === 'critical') return 'bg-red-500 dark:bg-red-600';
  return 'bg-border-strong';
};

export default function RiskRegister({ onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService.getAuditRisks()
      .then(setData)
      .catch(() => setData({ summary: { total: 0 }, risks: [] }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside className="relative w-full max-w-4xl h-full bg-surface border-l border-border-subtle overflow-auto">
        <header className="sticky top-0 bg-surface border-b border-border-subtle px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-label tracking-widest text-ink-muted uppercase">
              {t('traceability:risks.drawerTitle')}
            </div>
            <h2 className="text-base font-semibold text-ink mt-1">
              {t('traceability:risks.subtitle')}
            </h2>
          </div>
          <button type="button" onClick={onClose}
                  className="text-ink-secondary hover:text-ink px-2" aria-label="Close">✕</button>
        </header>

        <div className="p-6">
          {loading && <div className="text-ink-muted">···</div>}
          {!loading && data && (
            <>
              <RegisterSummary summary={data.summary} />
              <div className="mt-6 space-y-4">
                {(data.risks || []).map((r) => (
                  <RiskCard key={r.id} risk={r} />
                ))}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function RegisterSummary({ summary }) {
  const { t } = useTranslation();
  const s = summary || {};
  const counts = s.counts || {};
  return (
    <section className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <div className="text-label tracking-widest text-ink-muted uppercase mb-3">
        {t('traceability:risks.summary')}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label={t('traceability:risks.stateMitigated')}
          value={counts.mitigated ?? 0}
          tone="green"
        />
        <Stat
          label={t('traceability:risks.stateInProgress')}
          value={counts.in_progress ?? 0}
          tone="yellow"
        />
        <Stat
          label={t('traceability:risks.stateAccepted')}
          value={counts.accepted ?? 0}
          tone="neutral"
        />
        <Stat
          label={t('traceability:risks.stateUnmitigated')}
          value={counts.unmitigated ?? 0}
          tone={(counts.unmitigated ?? 0) > 0 ? 'red' : 'neutral'}
        />
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-y-1 text-xs text-ink-secondary">
        <dt className="text-ink-muted">{t('traceability:risks.lastReview')}</dt>
        <dd>{s.last_register_review || '—'}</dd>
        <dt className="text-ink-muted">{t('traceability:risks.nextReview')}</dt>
        <dd>{s.next_register_review || '—'}</dd>
        <dt className="text-ink-muted">{t('traceability:risks.overdue')}</dt>
        <dd>{(s.overdue_ids || []).length
          ? <span className="text-red-600 dark:text-red-300">{(s.overdue_ids || []).join(', ')}</span>
          : '—'}</dd>
        <dt className="text-ink-muted">{t('traceability:risks.worstResidual')}</dt>
        <dd className="font-mono">{s.worst_residual_severity || '—'}</dd>
      </dl>
    </section>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = {
    green: 'text-emerald-600 dark:text-emerald-300',
    yellow: 'text-accent-text',
    red: 'text-red-600 dark:text-red-300',
    neutral: 'text-ink',
  }[tone] || 'text-ink';
  return (
    <div className="rounded border border-border-subtle bg-surface p-3">
      <div className="text-label tracking-widest text-ink-muted uppercase">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

function RiskCard({ risk }) {
  const { t } = useTranslation();
  const state = risk.state || 'unmitigated';
  const stateCls = STATE_STYLE[state] || STATE_STYLE.unmitigated;

  return (
    <article className="rounded-lg border border-border-subtle bg-surface-raised/40 p-5">
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-label tracking-widest text-ink-muted uppercase">{risk.id}</div>
          <h3 className="text-base font-semibold text-ink mt-1">{risk.title}</h3>
        </div>
        <span className={`text-label tracking-widest px-2 py-1 rounded border ${stateCls}`}>
          {t(`traceability:risks.state_${state}`, { defaultValue: state })}
        </span>
      </header>

      <p className="text-sm text-ink-secondary leading-relaxed mb-4">{risk.description}</p>

      <div className="mb-4">
        <div className="text-label tracking-widest text-ink-muted uppercase mb-2">
          {t('traceability:risks.severityRow')}
        </div>
        <SeverityBar
          label={t('traceability:risks.inherent')}
          severity={risk.inherent_severity}
          likelihood={risk.inherent_likelihood}
          muted
        />
        <SeverityBar
          label={t('traceability:risks.residual')}
          severity={risk.residual_severity}
          likelihood={risk.residual_likelihood}
        />
      </div>

      <div className="mb-4">
        <div className="text-label tracking-widest text-ink-muted uppercase mb-2">
          {t('traceability:risks.mitigations')} ({(risk.mitigations || []).length})
        </div>
        <ul className="space-y-1.5 text-sm">
          {(risk.mitigations || []).map((m) => (
            <li key={m.id} className="text-ink-secondary leading-relaxed">
              <span className="text-emerald-600 dark:text-emerald-400 mr-1">✓</span>
              <span className="text-ink-muted mr-1">[{m.id}]</span>
              {m.description}
              {m.code_ref && (
                <code className="ml-2 text-[11px] bg-surface px-1.5 py-0.5 rounded text-ink-muted">
                  {m.code_ref}
                </code>
              )}
            </li>
          ))}
        </ul>
      </div>

      {risk.pending_mitigations && risk.pending_mitigations.length > 0 && (
        <div className="mb-4">
          <div className="text-label tracking-widest text-ink-muted uppercase mb-2">
            {t('traceability:risks.pending')}
          </div>
          <ul className="space-y-1 text-sm">
            {risk.pending_mitigations.map((p, i) => (
              <li key={i} className="text-accent-text leading-relaxed">
                <span className="mr-1">○</span>{p}
              </li>
            ))}
          </ul>
        </div>
      )}

      {risk.tests && risk.tests.length > 0 && (
        <div className="mb-4">
          <div className="text-label tracking-widest text-ink-muted uppercase mb-2">
            {t('traceability:risks.tests')}
          </div>
          <ul className="space-y-1 text-xs">
            {risk.tests.map((tst, i) => (
              <li key={i} className="text-ink-secondary">
                <span className="text-ink-muted mr-1">[{tst.kind || 'manual'}]</span>
                {tst.name}
                {tst.evidence && (
                  <div className="text-ink-muted ml-5 mt-0.5">→ {tst.evidence}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <footer className="pt-3 border-t border-border-subtle text-xs text-ink-muted flex flex-wrap gap-x-4">
        <span>{t('traceability:risks.owner')}: {risk.owner || '—'}</span>
        <span>{t('traceability:risks.lastReview')}: {risk.last_reviewed || '—'}</span>
        <span>{t('traceability:risks.nextReview')}: {risk.next_review || '—'}</span>
      </footer>
    </article>
  );
}

function SeverityBar({ label, severity, likelihood, muted }) {
  const { t } = useTranslation();
  const width = SEVERITY_WIDTH[String(severity || '').toLowerCase()] ?? 5;
  const colour = SEVERITY_COLOR(severity);
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex items-center justify-between text-xs mb-0.5">
        <span className={muted ? 'text-ink-muted' : 'text-ink'}>{label}</span>
        <span className={muted ? 'text-ink-muted' : 'text-ink-secondary'}>
          {severity || '—'}
          {likelihood && <span className="text-ink-muted"> · {t('traceability:risks.likelihood')}: {likelihood}</span>}
        </span>
      </div>
      <div className="h-1.5 w-full bg-border-subtle rounded-full overflow-hidden">
        <div className={`h-full ${colour} transition-[width]`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}
